import axios, { type AxiosError } from "axios";
import axiosRetry from "axios-retry";

import { PoliwrathClient } from "./client/PoliwrathClient";
import { memoizedRefreshToken } from "./experiences/common/utils/refreshToken";
import authService from "./services/authService";

// Configure retry behavior on the global axios
axiosRetry(axios, {
  retries: 3,
  retryDelay: axiosRetry.exponentialDelay,
  retryCondition: (error: AxiosError): boolean => {
    const status = error.response?.status;

    return Boolean(
      axiosRetry.isNetworkError(error) ||
        error.code === "ERR_NETWORK" ||
        status === 0 || // Have seen axios throw these for network errors
        status === 429 || // Too many requests
        (status && status >= 501 && status <= 599) // Cover the variety of non 500's
    );
  },
  onRetry: (retryCount, error, requestConfig) => {
    const maxRetries = requestConfig["axios-retry"]?.retries || 3;
    const delay = axiosRetry.exponentialDelay(retryCount);
    console.warn(
      `[Retry ${retryCount}/${maxRetries}] ${requestConfig.method?.toUpperCase()} ${
        requestConfig.url
      } (waiting ${delay}ms)`,
      {
        error: error.message,
        status: error.response?.status,
        code: error.code,
        timestamp: new Date().toISOString(),
      }
    );
  },
});

// Add auth token to all requests
axios.interceptors.request.use(
  async (config) => {
    const session = authService.getSession();
    if (session?.accessToken) {
      config.headers.setAuthorization(`Bearer ${session.accessToken}`);
    }
    return config;
  },
  async (error) => await Promise.reject(error)
);

// Handle token refresh for 401s
axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error?.config;

    if (error?.response?.status === 401 && !config?.sent) {
      config.sent = true;
      try {
        const result = await memoizedRefreshToken();
        if (result?.accessToken) {
          config.headers.setAuthorization(`Bearer ${result.accessToken}`);
          return await axios(config);
        }
      } catch (refreshError) {
        console.error("Token refresh failed:", refreshError);
        return await Promise.reject(refreshError);
      }
    }

    return await Promise.reject(error);
  }
);

// Create the client with just the BASE URL
const backendClient = new PoliwrathClient({
  BASE: import.meta.env.VITE_API_URL,
});

export default backendClient;
